import { observer } from "mobx-react";
import React from "react";
import {
  Modal,
  Stack,
  Center,
  Card,
  Alert,
  Text,
  Button,
  Select,
  Loader,
  Title,
  Group,
  ActionIcon,
  CopyButton,
  Badge,
  Anchor,
} from "@mantine/core";
import {
  IconAbacus,
  IconAlertTriangle,
  IconCheck,
  IconCopy,
  IconExternalLink,
  IconLink,
} from "@tabler/icons-react";

import { MODAL_WIDTH_FOR_DATA_INTENSIVE_MODALS } from "../../../helpers/constants";
import { BOB_MODALS, currencyCodes } from "../../helpers/constants";
import { Bob } from "../../stores/Bob";
import { CustomModalTitle } from "../details/customModalTitle";
import { OnchainTransactionViewOutput } from "../../helpers/onchainInboundTransactions";
import { OnchainOutboundTransactionViewOutput } from "../../helpers/onchainOutboundTransactions";
import { StablePaymentViewOutput } from "../../helpers/stablePayments";

// Type guard to check if data is an onchain inbound transaction
const isOnchainInboundTransaction = (
  data: any,
): data is OnchainTransactionViewOutput => {
  return (
    data &&
    "transactionId" in data &&
    "receivingAddress" in data &&
    "confirmations" in data
  );
};

// Type guard to check if data is an onchain outbound transaction
const isOnchainOutboundTransaction = (
  data: any,
): data is OnchainOutboundTransactionViewOutput => {
  return (
    data &&
    "destinationAddress" in data &&
    "totalDeductedSatoshis" in data &&
    !("amountStable" in data)
  );
};

// Type guard to check if data is a stable payment (USDT, etc.)
const isStablePayment = (data: any): data is StablePaymentViewOutput => {
  return (
    data &&
    "amountStable" in data &&
    "kindLabel" in data &&
    "explorerTxUrl" in data
  );
};

const ViewTransaction = observer(({ bob }: { bob: Bob }) => {
  const handleClose = () => {
    bob.setSelectedTransactionToView(null);
  };

  if (!bob.selectedTransactionToView?.data) {
    return null;
  }

  const data = bob.transactionViewData;

  if (!data) {
    return null;
  }

  const isOnchainInbound = isOnchainInboundTransaction(data);
  const isOnchainOutbound = isOnchainOutboundTransaction(data);
  const isStable = isStablePayment(data);
  const isOnchain = isOnchainInbound || isOnchainOutbound;
  const onchainInboundData = isOnchainInbound ? data : null;
  const onchainOutboundData = isOnchainOutbound ? data : null;
  const stablePaymentData = isStable ? data : null;

  return (
    <Modal
      opened={bob.openModal === BOB_MODALS.VIEW_TRANSACTION}
      onClose={handleClose}
      size={MODAL_WIDTH_FOR_DATA_INTENSIVE_MODALS}
      title={
        <CustomModalTitle
          text={
            isOnchain
              ? "Onchain Transaction"
              : isStable
                ? "Stablecoin Transaction"
                : "Rizful Transaction"
          }
          icon={isOnchain ? <IconLink /> : <IconAbacus />}
        />
      }
    >
      <Center>
        <Stack>
          <Title order={2}>{data.header}</Title>

          {/* Onchain Inbound: Confirmation status */}
          {isOnchainInbound && onchainInboundData && (
            <Group gap="xs">
              <Badge
                color={
                  onchainInboundData.isConsideredConfirmed ? "green" : "orange"
                }
                size="lg"
              >
                {onchainInboundData.isConsideredConfirmed
                  ? "Confirmed"
                  : `${onchainInboundData.confirmations} confirmations`}
              </Badge>
            </Group>
          )}

          {/* Onchain Outbound: Broadcast status */}
          {isOnchainOutbound && onchainOutboundData && (
            <Group gap="xs">
              <Badge
                color={onchainOutboundData.transactionId ? "green" : "orange"}
                size="lg"
              >
                {onchainOutboundData.transactionId ? "Broadcast" : "Pending"}
              </Badge>
            </Group>
          )}

          {/* Stable Payment: Broadcast status */}
          {isStable && stablePaymentData && (
            <Group gap="xs">
              <Badge
                color={stablePaymentData.transactionId ? "green" : "orange"}
                size="lg"
              >
                {stablePaymentData.transactionId ? "Broadcast" : "Pending"}
              </Badge>
            </Group>
          )}

          <Title order={4}>Date</Title>
          <Text>{data.createdAtString}</Text>

          <Title order={4}>Amount</Title>
          <Text>
            {data.satoshisString} ({data.fiatString})
          </Text>

          <Title order={4}>Exchange Rate</Title>
          <Text>{data.exchangeRateString}</Text>

          {/* Onchain Inbound: Transaction ID with mempool link and copy */}
          {isOnchainInbound && onchainInboundData && (
            <>
              <Title order={4}>Transaction ID</Title>
              <Group gap="xs" wrap="wrap">
                <Text
                  size="sm"
                  style={{
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    maxWidth: "100%",
                  }}
                >
                  {onchainInboundData.transactionId.slice(0, 16)}...
                  {onchainInboundData.transactionId.slice(-8)}
                </Text>
                <Group gap={4}>
                  <CopyButton value={onchainInboundData.transactionId}>
                    {({ copied, copy }) => (
                      <ActionIcon
                        size="sm"
                        variant={copied ? "filled" : "subtle"}
                        color={copied ? "teal" : "blue"}
                        onClick={copy}
                        aria-label="copy transaction id"
                      >
                        {copied ? (
                          <IconCheck size={14} />
                        ) : (
                          <IconCopy size={14} />
                        )}
                      </ActionIcon>
                    )}
                  </CopyButton>
                  <ActionIcon
                    component="a"
                    href={onchainInboundData.mempoolTxUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                    variant="subtle"
                    color="blue"
                    aria-label="view on mempool.space"
                  >
                    <IconExternalLink size={14} />
                  </ActionIcon>
                </Group>
              </Group>
            </>
          )}

          {/* Onchain Outbound: Transaction ID (may be null if pending) */}
          {isOnchainOutbound &&
            onchainOutboundData &&
            onchainOutboundData.transactionId && (
              <>
                <Title order={4}>Transaction ID</Title>
                <Group gap="xs" wrap="wrap">
                  <Text
                    size="sm"
                    style={{
                      fontFamily: "monospace",
                      wordBreak: "break-all",
                      maxWidth: "100%",
                    }}
                  >
                    {onchainOutboundData.transactionId.slice(0, 16)}...
                    {onchainOutboundData.transactionId.slice(-8)}
                  </Text>
                  <Group gap={4}>
                    <CopyButton value={onchainOutboundData.transactionId}>
                      {({ copied, copy }) => (
                        <ActionIcon
                          size="sm"
                          variant={copied ? "filled" : "subtle"}
                          color={copied ? "teal" : "blue"}
                          onClick={copy}
                          aria-label="copy transaction id"
                        >
                          {copied ? (
                            <IconCheck size={14} />
                          ) : (
                            <IconCopy size={14} />
                          )}
                        </ActionIcon>
                      )}
                    </CopyButton>
                    <ActionIcon
                      component="a"
                      href={onchainOutboundData.mempoolTxUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      variant="subtle"
                      color="blue"
                      aria-label="view on mempool.space"
                    >
                      <IconExternalLink size={14} />
                    </ActionIcon>
                  </Group>
                </Group>
              </>
            )}

          {/* Onchain Inbound: Receiving Address with copy */}
          {isOnchainInbound && onchainInboundData && (
            <>
              <Title order={4}>Receiving Address</Title>
              <Group gap="xs" wrap="wrap">
                <Text
                  size="sm"
                  style={{
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    maxWidth: "100%",
                  }}
                >
                  {onchainInboundData.receivingAddress.slice(0, 12)}...
                  {onchainInboundData.receivingAddress.slice(-8)}
                </Text>
                <Group gap={4}>
                  <CopyButton value={onchainInboundData.receivingAddress}>
                    {({ copied, copy }) => (
                      <ActionIcon
                        size="sm"
                        variant={copied ? "filled" : "subtle"}
                        color={copied ? "teal" : "blue"}
                        onClick={copy}
                        aria-label="copy receiving address"
                      >
                        {copied ? (
                          <IconCheck size={14} />
                        ) : (
                          <IconCopy size={14} />
                        )}
                      </ActionIcon>
                    )}
                  </CopyButton>
                  <ActionIcon
                    component="a"
                    href={onchainInboundData.mempoolAddressUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                    variant="subtle"
                    color="blue"
                    aria-label="view address on mempool.space"
                  >
                    <IconExternalLink size={14} />
                  </ActionIcon>
                </Group>
              </Group>
            </>
          )}

          {/* Onchain Outbound: Destination Address with copy */}
          {isOnchainOutbound && onchainOutboundData && (
            <>
              <Title order={4}>Destination Address</Title>
              <Group gap="xs" wrap="wrap">
                <Text
                  size="sm"
                  style={{
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    maxWidth: "100%",
                  }}
                >
                  {onchainOutboundData.destinationAddress.slice(0, 12)}...
                  {onchainOutboundData.destinationAddress.slice(-8)}
                </Text>
                <Group gap={4}>
                  <CopyButton value={onchainOutboundData.destinationAddress}>
                    {({ copied, copy }) => (
                      <ActionIcon
                        size="sm"
                        variant={copied ? "filled" : "subtle"}
                        color={copied ? "teal" : "blue"}
                        onClick={copy}
                        aria-label="copy destination address"
                      >
                        {copied ? (
                          <IconCheck size={14} />
                        ) : (
                          <IconCopy size={14} />
                        )}
                      </ActionIcon>
                    )}
                  </CopyButton>
                  <ActionIcon
                    component="a"
                    href={onchainOutboundData.mempoolAddressUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                    variant="subtle"
                    color="blue"
                    aria-label="view address on mempool.space"
                  >
                    <IconExternalLink size={14} />
                  </ActionIcon>
                </Group>
              </Group>
            </>
          )}

          {/* Stable Payment: Transaction ID with Solscan link */}
          {isStable && stablePaymentData && stablePaymentData.transactionId && (
            <>
              <Title order={4}>Transaction ID</Title>
              <Group gap="xs" wrap="wrap">
                <Text
                  size="sm"
                  style={{
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    maxWidth: "100%",
                  }}
                >
                  {stablePaymentData.transactionId.slice(0, 16)}...
                  {stablePaymentData.transactionId.slice(-8)}
                </Text>
                <Group gap={4}>
                  <CopyButton value={stablePaymentData.transactionId}>
                    {({ copied, copy }) => (
                      <ActionIcon
                        size="sm"
                        variant={copied ? "filled" : "subtle"}
                        color={copied ? "teal" : "blue"}
                        onClick={copy}
                        aria-label="copy transaction id"
                      >
                        {copied ? (
                          <IconCheck size={14} />
                        ) : (
                          <IconCopy size={14} />
                        )}
                      </ActionIcon>
                    )}
                  </CopyButton>
                  {stablePaymentData.explorerTxUrl && (
                    <ActionIcon
                      component="a"
                      href={stablePaymentData.explorerTxUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      variant="subtle"
                      color="blue"
                      aria-label="view on Solscan"
                    >
                      <IconExternalLink size={14} />
                    </ActionIcon>
                  )}
                </Group>
              </Group>
            </>
          )}

          {/* Stable Payment: Destination Address with Solscan link */}
          {isStable && stablePaymentData && (
            <>
              <Title order={4}>Destination Address</Title>
              <Group gap="xs" wrap="wrap">
                <Text
                  size="sm"
                  style={{
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    maxWidth: "100%",
                  }}
                >
                  {stablePaymentData.destinationAddress.slice(0, 12)}...
                  {stablePaymentData.destinationAddress.slice(-8)}
                </Text>
                <Group gap={4}>
                  <CopyButton value={stablePaymentData.destinationAddress}>
                    {({ copied, copy }) => (
                      <ActionIcon
                        size="sm"
                        variant={copied ? "filled" : "subtle"}
                        color={copied ? "teal" : "blue"}
                        onClick={copy}
                        aria-label="copy destination address"
                      >
                        {copied ? (
                          <IconCheck size={14} />
                        ) : (
                          <IconCopy size={14} />
                        )}
                      </ActionIcon>
                    )}
                  </CopyButton>
                  {stablePaymentData.explorerAddressUrl && (
                    <ActionIcon
                      component="a"
                      href={stablePaymentData.explorerAddressUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      variant="subtle"
                      color="blue"
                      aria-label="view address on Solscan"
                    >
                      <IconExternalLink size={14} />
                    </ActionIcon>
                  )}
                </Group>
              </Group>
            </>
          )}

          {/* Stable Payment: Platform Fee */}
          {isStable &&
            stablePaymentData &&
            stablePaymentData.platformFeeSatoshis > 0 && (
              <>
                <Title order={4}>Platform Fee</Title>
                <Text>
                  {stablePaymentData.platformFeeSatoshis.toLocaleString()}{" "}
                  satoshi
                  {stablePaymentData.platformFeeSatoshis === 1 ? "" : "s"}
                </Text>
              </>
            )}

          {/* Stable Payment: Total Deducted */}
          {isStable && stablePaymentData && (
            <>
              <Title order={4}>Total Deducted</Title>
              <Text>
                {stablePaymentData.totalDeductedSatoshis.toLocaleString()}{" "}
                satoshi
                {stablePaymentData.totalDeductedSatoshis === 1 ? "" : "s"}
              </Text>
            </>
          )}

          {/* Onchain Outbound: Platform Fee */}
          {isOnchainOutbound &&
            onchainOutboundData &&
            onchainOutboundData.platformFeeSatoshis > 0 && (
              <>
                <Title order={4}>Platform Fee</Title>
                <Text>
                  {onchainOutboundData.platformFeeSatoshis.toLocaleString()}{" "}
                  satoshi
                  {onchainOutboundData.platformFeeSatoshis === 1 ? "" : "s"}
                </Text>
              </>
            )}

          {/* Onchain Outbound: Total Deducted */}
          {isOnchainOutbound && onchainOutboundData && (
            <>
              <Title order={4}>Total Deducted</Title>
              <Text>
                {onchainOutboundData.totalDeductedSatoshis.toLocaleString()}{" "}
                satoshi
                {onchainOutboundData.totalDeductedSatoshis === 1 ? "" : "s"}
              </Text>
            </>
          )}

          {/* Regular transaction fields */}
          {!isOnchain &&
            !isStable &&
            data.platformFeeSatoshis !== null &&
            data.platformFeeSatoshis > 0 && (
              <>
                <Title order={4}>Platform Fee</Title>
                <Text>
                  {data.platformFeeSatoshis.toLocaleString()} satoshi
                  {data.platformFeeSatoshis === 1 ? "" : "s"} (
                  {data.platformFeeFiatString})
                </Text>
              </>
            )}

          {!isOnchain && !isStable && data.routingFeeSatoshis !== null && (
            <>
              <Title order={4}>Routing Fee</Title>
              <Text>
                {data.routingFeeSatoshis.toLocaleString()} satoshi
                {data.routingFeeSatoshis === 1 ? "" : "s"} (
                {data.routingFeeFiatString})
              </Text>
            </>
          )}

          {!isOnchain && !isStable && data.invoice && (
            <>
              <Title order={4}>Invoice</Title>
              <Group align="center" gap="xs">
                <Text>{data.invoice.slice(0, 20)}...</Text>
                <CopyButton value={data.invoice}>
                  {({ copied, copy }) => (
                    <ActionIcon
                      size="lg"
                      variant={copied ? "filled" : "default"}
                      color={copied ? "teal" : "blue"}
                      onClick={copy}
                      aria-label="copy invoice"
                    >
                      {copied ? <IconCheck /> : <IconCopy />}
                    </ActionIcon>
                  )}
                </CopyButton>
              </Group>
            </>
          )}

          {!isOnchain && !isStable && data.lightningAddress && (
            <>
              <Title order={4}>Lightning Address</Title>
              <Text>{data.lightningAddress}</Text>
            </>
          )}

          {data.comment && (
            <>
              <Title order={4}>Comment</Title>
              <Text>{data.comment}</Text>
            </>
          )}

          {!isOnchain && !isStable && (
            <>
              <Title order={4}>ID</Title>
              <Text>{data.id}</Text>
            </>
          )}
        </Stack>
      </Center>
    </Modal>
  );
});

export default ViewTransaction;
